import { Link } from "react-router-dom";
import { useScrollRevealChildren } from "../../../hooks/useScrollReveal";

export interface FinalCTAContent {
  badge?: string;
  headline: string;
  headlineAccent?: string;
  description: string;
  primaryCta: string;
  primaryCtaPath: string;
  secondaryCta: string;
  secondaryCtaHref: string;
  backgroundImage: string;
}

interface FinalCTASectionProps {
  content: FinalCTAContent;
}

export default function FinalCTASection({ content }: FinalCTASectionProps) {
  const {
    badge,
    headline,
    headlineAccent,
    description,
    primaryCta,
    primaryCtaPath,
    secondaryCta,
    secondaryCtaHref,
    backgroundImage,
  } = content;
  const ref = useScrollRevealChildren();

  return (
    <section className="relative py-40 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        />
        {/* Deep cinematic overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/80 to-black/50" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50" />
        {/* Warm amber tint */}
        <div className="absolute inset-0 bg-[#2A1A00]/20" />
      </div>

      {/* Left gold border accent */}
      <div className="absolute left-0 top-0 bottom-0 w-[4px] bg-gradient-to-b from-transparent via-[#C9A961] to-transparent opacity-70" />

      {/* Decorative KRP monogram — right */}
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 text-white/[0.03] select-none font-bold leading-none hidden xl:block"
        style={{ fontFamily: "'Playfair Display', serif", fontSize: "28vw" }}
        aria-hidden="true"
      >
        K
      </div>

      <div ref={ref} className="relative z-10 max-w-screen-xl mx-auto px-6 xl:px-10">
        <div className="max-w-[680px]">
          {badge && (
            <div className="reveal flex items-center gap-4 mb-9">
              <div className="h-px w-14 bg-gradient-to-r from-[#C9A961] to-[#C9A961]/30" />
              <span
                className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase font-medium"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {badge}
              </span>
            </div>
          )}

          <h2
            className="reveal delay-100 text-[56px] md:text-[70px] xl:text-[84px] text-white leading-[0.96] mb-6"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, letterSpacing: "-0.02em" }}
          >
            {headline}
            {headlineAccent && (
              <em className="block text-[#C9A961]" style={{ fontStyle: "italic" }}>
                {headlineAccent}
              </em>
            )}
          </h2>

          <p
            className="reveal delay-200 text-white/45 text-base md:text-lg leading-relaxed mb-12"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
          >
            {description}
          </p>

          <div className="reveal delay-300 flex flex-wrap gap-4 mb-14">
            <Link
              to={primaryCtaPath}
              className="group relative overflow-hidden bg-[#C9A961] text-white text-[10px] tracking-[0.22em] uppercase font-semibold px-12 py-5 hover:bg-[#b8964f] transition-all duration-500 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <span className="relative z-10">{primaryCta}</span>
              <div className="absolute inset-0 bg-white/10 -translate-x-full group-hover:translate-x-0 transition-transform duration-500 skew-x-12" />
            </Link>
            <a
              href={secondaryCtaHref}
              className="flex items-center gap-3 border border-white/30 text-white text-[10px] tracking-[0.22em] uppercase font-medium px-10 py-5 hover:border-[#C9A961] hover:text-[#C9A961] transition-all duration-400 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-sm" />
              </div>
              {secondaryCta}
            </a>
          </div>

          {/* Trust row */}
          <div className="reveal delay-400 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/8 pt-8">
            {[
              { icon: "ri-shield-check-line", label: "Licensed & Insured" },
              { icon: "ri-star-half-line", label: "4.9-Star Rated" },
              { icon: "ri-home-heart-line", label: "Family-Owned" },
              { icon: "ri-map-pin-2-line", label: "Boca Raton Showroom" },
              { icon: "ri-price-tag-3-line", label: "From $9,995" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${item.icon} text-[#C9A961] text-[13px]`} />
                </div>
                <span
                  className="text-white/40 text-[11px] tracking-wide"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
